"use client";

import { useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import gsap from "gsap";

const AmbientCanvas = dynamic(
  () => import("@/components/three/AmbientCanvas"),
  { ssr: false, loading: () => <div className="w-full h-full bg-black/20 animate-pulse" /> }
);

export default function DesignShowcase() {
  const gridRef = useRef<SVGSVGElement>(null);
  const scanLineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!gridRef.current || !scanLineRef.current) return;

    // GSAP Animation for scanning lines
    gsap.to(scanLineRef.current, {
      top: "100%",
      duration: 4,
      repeat: Infinity,
      ease: "power1.inOut",
    });

    // GSAP Animation for grid pulsing
    gsap.to(gridRef.current, {
      opacity: 0.4,
      duration: 2,
      repeat: Infinity,
      yoyo: true,
      ease: "sine.inOut",
    });
  }, []);

  return (
    <div className="relative w-full h-full overflow-hidden bg-[#0A0908] group">
      {/* 3D Scene Layer */}
      <div className="absolute inset-0 z-0 opacity-80 scale-125 group-hover:scale-150 transition-transform duration-[10s] ease-out">
        <AmbientCanvas />
      </div>

      {/* SVG Architectural Grid Overlay */}
      <svg
        ref={gridRef}
        className="absolute inset-0 w-full h-full z-10 opacity-15 pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#C9A84C" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {/* Pulsing Orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(201,168,76,0.3) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* Scanning Line */}
      <div
        ref={scanLineRef}
        className="absolute top-0 left-0 w-full h-[2px] z-20 pointer-events-none"
        style={{
          background: "linear-gradient(to right, transparent, #C9A84C, transparent)",
          boxShadow: "0 0 15px #C9A84C",
          opacity: 0.3,
        }}
      />

      {/* Corner HUD markers */}
      <div className="absolute top-6 left-6 w-10 h-10 flex flex-col justify-between z-30">
        <div className="w-full h-[1px] bg-[#C9A84C] opacity-50" />
        <div className="w-[1px] h-full bg-[#C9A84C] opacity-50" />
      </div>
      <div className="absolute bottom-6 right-6 w-10 h-10 flex flex-col items-end justify-end z-30">
        <div className="w-[1px] h-full bg-[#C9A84C] opacity-50" />
        <div className="w-full h-[1px] bg-[#C9A84C] opacity-50" />
      </div>

      {/* Aesthetic Design labels (Minimal) */}
      <div className="absolute bottom-8 left-8 z-30 flex flex-col gap-1">
        <div className="flex gap-1">
          <div className="w-3 h-3 bg-[#C9A84C]" />
          <div className="w-3 h-3 bg-[#F5F0E8] opacity-50" />
        </div>
        <span className="text-[8px] tracking-[0.5em] uppercase text-[#C9A84C] opacity-40 font-mono">
          Aurelius_Motion_Study_01
        </span>
      </div>
    </div>
  );
}
